import type { TagCount } from '@mollusk/shared';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { TagChip } from '../../components/TagChip';

type Scope = 'address' | 'street' | 'zip' | 'city';
const SCOPES: { key: Scope; label: string }[] = [
  { key: 'address', label: 'Address' },
  { key: 'street', label: 'Street' },
  { key: 'zip', label: 'ZIP' },
  { key: 'city', label: 'City' },
];

interface Cell {
  unique_addresses: number;
  unique_permits: number;
  top_tags: TagCount[];
}

/** Counts box (#171): pick a scope; see its permit total and top six tags. */
export function CountsBox({
  labels,
  address,
  counts,
  onShowAddresses,
}: {
  labels: Record<Scope, string>;
  /** Null until the address's permits have loaded, so a failure doesn't read as 0. */
  address: { total: number; top_tags: TagCount[] } | null;
  counts: { street: Cell; zip: Cell; city: Cell } | undefined;
  /** Open the scope's unique-address list (street/ZIP/city). */
  onShowAddresses?: (scope: Exclude<Scope, 'address'>) => void;
}) {
  const [scope, setScope] = useState<Scope>('address');
  const cell =
    scope === 'address'
      ? address
      : counts
        ? { total: counts[scope].unique_permits, top_tags: counts[scope].top_tags }
        : null;
  const max = Math.max(1, ...(cell?.top_tags.map((t) => t.count) ?? [1]));
  return (
    <Paper sx={{ p: 2 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={scope}
        onChange={(_, v: Scope | null) => v && setScope(v)}
        aria-label="Counts scope"
      >
        {SCOPES.map((s) => (
          <ToggleButton key={s.key} value={s.key}>
            {s.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {labels[scope]}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {cell ? `${cell.total.toLocaleString()} permits` : '—'}
      </Typography>
      {scope !== 'address' && counts && onShowAddresses ? (
        <Link component="button" variant="body2" onClick={() => onShowAddresses(scope)}>
          at {counts[scope].unique_addresses.toLocaleString()} addresses
        </Link>
      ) : null}
      <Box sx={{ mt: 1, display: 'grid', gap: 0.75 }}>
        {(cell?.top_tags ?? []).map((t) => (
          <Box
            key={t.tag}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(120px, 180px) 1fr 70px',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Box>
              <TagChip tag={t.tag} />
            </Box>
            <LinearProgress
              variant="determinate"
              value={(t.count / max) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {t.count.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
